import {
  keepPreviousData,
  useMutation,
  UseMutationOptions,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import api from "@/lib/axiosConfig";
import {
  FetchedMultipleprint,
  Multipleprint,
  MultipleprintCoreInfo,
} from "@/lib/types";
import { parseJsonOrUndefined } from "@/lib/utils";
import { PaginatedReturn, PagingParameters, RelativePosition } from "../dtos";

export const baseRoute = "back/api/multipleprint";

interface MultiplePrintDTO {
  where: any;
  order: any;
  take: number;
  skip: number;
}

export const mapFetchedMultiplePrint = (
  multipleprint: FetchedMultipleprint,
): Multipleprint => ({
  ...multipleprint,
  //TECH_DEBT
  estampesMultiplesDescriptionDimensionsOeuvre: parseJsonOrUndefined(
    multipleprint.estampesMultiplesDescriptionDimensionsOeuvre,
  ),
  estampesMultiplesDescriptionDimensionsImage: parseJsonOrUndefined(
    multipleprint.estampesMultiplesDescriptionDimensionsImage,
  ),
});

async function getMultiplePrints({
  where,
  order,
  take,
  skip,
}: MultiplePrintDTO): Promise<Multipleprint[]> {
  const { data } = await api.post<FetchedMultipleprint[]>(
    `back/api/multipleprint?take=${take || 10}&skip=${skip || 0}`,
    {
      where,
      orderBy: order,
    },
  );
  return data.map(mapFetchedMultiplePrint);
}

export const useGetAllMultiplePrints = ({
  where,
  order,
  take,
  skip,
}: MultiplePrintDTO) => {
  return useQuery({
    queryKey: ["multipleprints", where, order, take, skip],
    queryFn: () => getMultiplePrints({ where, order, take, skip }),
    placeholderData: keepPreviousData,
  });
};

export async function getCountByStatus(where?: object) {
  const { data } = await api.post(`back/api/multipleprint/countByStatus`, {
    where,
  });
  return data;
}

export const getRelativePosition = async (
  id: string,
  order: object,
  where: object,
) => {
  const { data } = await api.post<RelativePosition>(
    `${baseRoute}/${id}/position`,
    {
      order,
      where,
    },
  );
  return data;
};

export const useCountByStatus = () => {
  return useQuery({
    queryKey: ["countByStatus", "multipleprint"],
    queryFn: () => getCountByStatus(),
  });
};

export const getMultiplePrint = async (id: string): Promise<Multipleprint> => {
  const { data } = await api.get<FetchedMultipleprint>(
    `back/api/multipleprint/${id}`,
  );

  return mapFetchedMultiplePrint(data);
};

export const useGetMultiplePrint = (id?: string) => {
  return useQuery({
    queryKey: ["multipleprint", id],
    queryFn: () => (id ? getMultiplePrint(id) : undefined),
  });
};

async function updateMultiplePrint(
  multiplePrint: Multipleprint & { relatedAuthorIds?: string[] },
) {
  const {
    instanceId: _iid,
    relatedArtwork: _ra,
    relatedEnsemble: _re,
    relatedAuthor: _rau,
    relatedMultipleprint: _rmp,
    relatedMultiplesample: _rms,
    from_Multipleprint_relatedMultipleprint: _fmrm,
    multipleprintInAuctions: _mia,
    multipleprintInBibliography: _mib,
    multipleprintInExhibition: _mie,
    multipleprintInMultipleSample: _mims,
    collectorInMultipleprint: _cim,
    updatedById: _ubid,
    createdById: _cbid,
    createdBy: _cb,
    createdAt: _ca,
    ...rest
  } = multiplePrint;
  return await api.patch(`back/api/multipleprint/${multiplePrint.id}`, {
    data: {
      ...rest,
      estampesMultiplesDescriptionDimensionsOeuvre: JSON.stringify(
        rest.estampesMultiplesDescriptionDimensionsOeuvre,
      ),
      estampesMultiplesDescriptionDimensionsImage: JSON.stringify(
        rest.estampesMultiplesDescriptionDimensionsImage,
      ),
    },
  });
}

export function useUpdateMultiplePrint() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: updateMultiplePrint,
    onSuccess: (_, v) => {
      queryClient.invalidateQueries({ queryKey: ["multipleprint", v.id] });
      queryClient.invalidateQueries({ queryKey: ["multipleprints"] });
    },
  });
}

type CreateMultipleprintParams = {
  estampesMultiplesDescriptionNumeroEnregistrement: string;
  estampesMultiplesDescriptionTitre: string;
};
type CreateMultipleprintResponse = Pick<Multipleprint, "id">;

const createMultipleprint = async ({
  estampesMultiplesDescriptionNumeroEnregistrement,
  estampesMultiplesDescriptionTitre,
}: CreateMultipleprintParams) => {
  return await api
    .post<CreateMultipleprintResponse>("back/api/multipleprint/create", {
      estampesMultiplesDescriptionNumeroEnregistrement,
      estampesMultiplesDescriptionTitre,
    })
    .then((res) => res.data);
};

export const useCreateMultipleprint = (
  options?: UseMutationOptions<
    CreateMultipleprintResponse,
    Error,
    CreateMultipleprintParams
  >,
) => {
  const queryClient = useQueryClient();

  return useMutation({
    ...options,
    mutationFn: createMultipleprint,
    onSuccess: (data, vars, ctx) => {
      queryClient.invalidateQueries({ queryKey: ["multipleprints"] });
      queryClient.invalidateQueries({ queryKey: ["multipleprint", data.id] });
      queryClient.invalidateQueries({
        queryKey: ["countByStatus", "multipleprint"],
      });
      queryClient.invalidateQueries({ queryKey: ["instance", "count-data"] });
      options?.onSuccess?.(data, vars, ctx);
    },
  });
};

export const getAllMultipleprintsCoreInfo = async (
  page: PagingParameters,
  search?: string,
) => {
  const response = await api.get<PaginatedReturn<MultipleprintCoreInfo>>(
    baseRoute + "/core-info",
    {
      params: {
        ...page,
        search,
      },
    },
  );
  return response.data;
};
export const useGetAllMultipleprintsCoreInfo = (
  page: PagingParameters,
  search?: string,
) => {
  return useQuery({
    queryKey: ["multipleprints", "core-info", page, search],
    queryFn: () => getAllMultipleprintsCoreInfo(page, search),
    placeholderData: keepPreviousData,
  });
};
